import React, { useMemo } from 'react';
import { useChangeAll } from './ChangeAllContext';
import { useChangeAllFields } from '../../../hooks/useChangeAllFields';

/**
 * Shown at the top of the PRO drawer ONLY in change-all mode. Summarizes the
 * bulk target set + how many fields are flagged "apply to all", and offers a
 * one-click "flag every edited field" and "clear". Renders nothing when
 * change-all mode is off.
 *
 * The count covers BOTH flag sets: the order-field checkboxes (Order Info / Addl Info /
 * Tracing / Addl Cost) and the Invoicing tab's own. It used to count only the first, so
 * a user who ticked "apply to all" beside Margin Approval read "0 fields flagged for
 * all" and had no way to tell whether the tick had registered before pressing Save.
 * Flagged invoice LINE ITEMS are counted separately — they aren't fields, and the
 * fan-out applies them through their own endpoint.
 */
const ChangeAllBanner: React.FC<{ editedOrderFields: Record<string, any> }> = ({
  editedOrderFields,
}) => {
  const {
    enabled, targetPros, anchorPro, checkedFields, selectAll, clearAll,
    checkedLegFields, checkedInvoiceFields, checkedInvoiceLines,
    isTemplate, fieldAllowed,
  } = useChangeAll();
  // Two gates, and note it is isSweepable rather than isFannable:
  //   registry    is Change All open for the field. Without this the sweep would tick
  //               fields an admin has closed, which the server now refuses.
  //   policy      on a template, is the field currently unlocked. Without this the
  //               sweep would tick fields whose checkbox was deliberately withheld and
  //               the save would refuse the whole record.
  // isSweepable is the STRICTER of the hook's two answers: a field can legitimately
  // have a checkbox on screen and still be one this button must never tick. Bulk
  // delete is that case — drawn, but armed only by its own modal.
  const { isSweepable } = useChangeAllFields(enabled);
  // No permission gate needed on the sweep: it only ever ticks fields the user has
  // ALREADY EDITED on the anchor, and a field they may not edit is one they could not
  // have edited. Per-field permission lives on each checkbox (BulkFieldCheckbox).
  const editedEligible = useMemo(
    () => Object.keys(editedOrderFields).filter(
      (n) => isSweepable(n) && (!isTemplate || fieldAllowed(n)),
    ),
    [editedOrderFields, isTemplate, fieldAllowed, isSweepable],
  );
  if (!enabled) return null;
  const n = targetPros.length;
  // Same drawer, two record families — say which one is in the set.
  const noun = isTemplate ? 'TEMPLATE' : 'PRO';
  // The two leg-LOCATION flags count as flagged fields here. Without them the
  // banner read "0 fields flagged for all" with a pickup location armed, and Clear
  // sat disabled over a flag the user could see on screen.
  const fieldCount =
    checkedFields.size + checkedLegFields.size + checkedInvoiceFields.size;
  const lineCount = checkedInvoiceLines.size;
  const nothingFlagged = fieldCount === 0 && lineCount === 0;
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-300 dark:border-amber-800 text-[0.75rem] text-amber-800 dark:text-amber-200">
      <span>
        <strong>Change All</strong> — anchor {noun} {String(anchorPro ?? '')} · {n} {noun}{n === 1 ? '' : 's'} in set · {fieldCount} field{fieldCount === 1 ? '' : 's'} flagged for all
        {lineCount > 0 && ` · ${lineCount} line item${lineCount === 1 ? '' : 's'}`}
      </span>
      <span className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => selectAll(editedEligible)}
          disabled={editedEligible.length === 0}
          className="px-2 py-0.5 rounded border border-amber-400 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Flag all edited ({editedEligible.length})
        </button>
        <button
          type="button"
          onClick={clearAll}
          disabled={nothingFlagged}
          className="px-2 py-0.5 rounded border border-amber-400 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Clear
        </button>
      </span>
    </div>
  );
};

export default ChangeAllBanner;
